<template>
  <Modal ref="modalRef" width="38rem" @after-close="reset">
    <!-- The header holds the title alone, as every dialog's does; what the step is about opens the body -->
    <template #header>{{ title }}</template>

    <template #body>
      <View v-if="step === 'pick'" direction="column" gap="md" class="form">
        <Text variant="body-medium" color="secondary">
          Pick the apartments that are physically inside this building. Each one keeps its listing, leads, showings and
          apply link.
        </Text>

        <FormField v-if="!props.building" label="Building">
          <Select v-model="chosenBuildingId" data-testid="attach-building">
            <option value="" disabled>Choose a building</option>
            <option v-for="candidate in buildings" :key="candidate.id" :value="candidate.id">
              {{ candidate.name }}
            </option>
          </Select>
        </FormField>

        <Input
          v-if="candidates.length >= searchFrom"
          v-model="search"
          type="search"
          placeholder="Search by address"
          aria-label="Search by address"
        >
          <template #prefix>
            <Icon name="search-lg" size="small" class="search-icon" />
          </template>
        </Input>

        <EmptyState
          v-if="!candidates.length"
          icon="home-03"
          title="No apartments to attach"
          description="Every apartment in your company is a unit of a building already, or is listed as a house."
        />

        <View
          v-for="group in groups"
          :key="group.title"
          direction="column"
          gap="sm"
          role="group"
          :aria-label="group.title"
        >
          <View gap="xs" align="center">
            <Text as="span" variant="label-small" color="secondary">{{ group.title }}</Text>
            <Badge v-if="group.count" :label="String(group.homes.length)" variant="grey" size="small" />
          </View>

          <ChecklistRow
            v-for="home in group.homes"
            :key="home.id"
            :id="`attach-${home.id}`"
            :title="home.displayName"
            :subtitle="subtitleOf(home)"
            :checked="home.id in picked"
            :disabled="Boolean(reasonNotToMove(home))"
            :data-testid="`attach-row-${home.id}`"
            @toggle="toggle(home.id, $event)"
          >
            <template #leading>
              <PropertyPhotoPreview :image-url="home.coverImage" size="sm" />
            </template>
            <template #trailing>
              <PropertyStatusLabel :status="home.status" small />
              <View v-if="home.id in picked" direction="column" gap="xs" class="number">
                <Input
                  v-model="picked[home.id].unitNumber"
                  size="small"
                  :name="`unit-number-${home.id}`"
                  placeholder="Unit #"
                  maxlength="10"
                  aria-label="Unit number"
                  :data-testid="`unit-number-${home.id}`"
                  @update:model-value="picked[home.id].error = ''"
                >
                  <template #prefix>#</template>
                </Input>
                <Text v-if="numberError(home.id)" as="span" variant="body-small" class="error">
                  {{ numberError(home.id) }}
                </Text>
              </View>
            </template>
          </ChecklistRow>
        </View>

        <Text v-if="candidates.length && !groups.length" variant="body-small" color="secondary">
          No apartments match that.
        </Text>
      </View>

      <View v-else direction="column" gap="md" class="form">
        <Link color="accent" class="back" @click.prevent="step = 'pick'">
          <Icon name="chevron-left" size="small" />
          <span>Back to the list</span>
        </Link>

        <View gap="sm" align="center">
          <View class="thumbs">
            <PropertyPhotoPreview v-for="home in chosen" :key="home.id" :image-url="home.coverImage" size="xs" />
          </View>
          <View direction="column" gap="xs" class="identity">
            <Text variant="label-medium">{{ summaryLine }}</Text>
            <Text variant="body-small" color="secondary">
              {{ chosen.length === 1 ? 'Becomes a unit of' : 'All become units of' }} {{ building?.name }}
            </Text>
          </View>
        </View>

        <LoadingIndicator v-if="detailsPending" />
        <View
          v-for="diff in amenityDiffs"
          :key="diff.home.id"
          direction="column"
          gap="xs"
          class="diff"
          :data-testid="`amenities-diff-${diff.home.id}`"
        >
          <View gap="sm" justify="space-between">
            <Text as="span" variant="label-small" color="secondary">
              Amenities that differ on {{ diff.home.displayName }}
            </Text>
            <Text as="span" variant="label-small" color="secondary">
              {{ diff.rows.length }} of {{ buildingAmenityKeys.length }}
            </Text>
          </View>
          <table class="diff-table">
            <thead>
              <tr>
                <th></th>
                <th>Home today</th>
                <th>Building</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in diff.rows" :key="row.label">
                <td>{{ row.label }}</td>
                <td :class="{ lost: row.home }">{{ row.home ? 'Yes' : '—' }}</td>
                <td>{{ row.building ? 'Yes' : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </View>

        <!-- The same "what to expect" card as the listing dialogs -->
        <WhatToExpectCard :items="whatToExpectItems" />

        <!-- The notice closes the step, as in the listing dialogs -->
        <Hint gap="md">
          <template #icon>
            <IconTile icon="lightbulb-02" size="small" color="yellow" />
          </template>

          <View gap="sm" direction="column">
            <Text variant="h5">Important notice</Text>
            <Text variant="body-medium">
              The building's address, amenities, parking, accessibility and access notes replace the home's, and the
              building's manager and portfolio take it over. Its listing, leads, showings, applications and apply link
              are untouched.{{ listedNotice }}
            </Text>
          </View>
        </Hint>
      </View>
    </template>

    <template #footer>
      <Text as="span" variant="body-small" color="secondary" class="status">
        {{ step === 'pick' ? pluralize('home', chosen.length, true) + ' selected' : 'Step 2 of 2' }}
      </Text>
      <!-- One pair at the end of the row; the footer would otherwise spread three children across it -->
      <View gap="sm" class="actions">
        <Button v-if="step === 'pick'" variant="ghost" @click="modalRef?.close()">Cancel</Button>
        <Button v-else variant="ghost" @click="step = 'pick'">Back</Button>
        <Button v-if="step === 'pick'" :disabled="!canContinue" data-testid="attach-continue" @click="confirm">
          Continue
        </Button>
        <Button v-else :loading="isAttaching" data-testid="attach-submit" @click="attach">
          Attach {{ pluralize('home', chosen.length, true) }}
        </Button>
      </View>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import pluralize from 'pluralize';
import { computed, reactive, ref, watch } from 'vue';

import {
  Badge,
  Button,
  ChecklistRow,
  EmptyState,
  FormField,
  Hint,
  Icon,
  IconTile,
  Input,
  Link,
  LoadingIndicator,
  Modal,
  Select,
  Text,
  View,
  WhatToExpectCard,
  WhatToExpectItem,
  extractErrorMessage,
  serverErrorCode,
  serverErrorDetail,
  useAlertsStore,
} from '@tallo/design-system';
import { runsUnit, teamMemberName, useCompanyPortfoliosQuery } from '@tallo/investor/company';
import { useInvestorStore } from '@tallo/investor/investor';
import {
  BuildingDto,
  PropertyListItemDto,
  PropertyStatusLabel,
  buildingAmenityKeys,
  buildingErrorCodes,
  propertyService,
  propertySummary,
  unitLabel,
  useAttachUnits,
  useBuildingUnitsQuery,
  useBuildingsQuery,
  usePortfolioQuery,
} from '@tallo/investor/property';
import { PropertyDto, PropertyPhotoPreview, PropertyStatus, PropertyType, amenityNamesMap } from '@tallo/property';
import { escapeHtml } from '@tallo/utility';

/**
 * Files standing apartments into a building as its units. Opened on the building's page with the
 * building given, or from a home's page with the home ticked and the building chosen here.
 */
const props = defineProps<{ building?: BuildingDto }>();
const emit = defineEmits<{ attached: [buildingId: string] }>();

// Below this many homes the whole list fits on screen, and a search box is one more thing to read
const searchFrom = 6;

const alertsStore = useAlertsStore();
const modalRef = ref<InstanceType<typeof Modal>>();
const { investor, isAdmin } = storeToRefs(useInvestorStore());
const { properties } = usePortfolioQuery();
const { portfolios } = useCompanyPortfoliosQuery();
const { buildings } = useBuildingsQuery(() => !props.building);
const { mutateAsync: attachUnits } = useAttachUnits();

const step = ref<'pick' | 'confirm'>('pick');
const chosenBuildingId = ref('');
const search = ref('');
const picked = reactive<Record<string, { unitNumber: string; error: string }>>({});
const isAttaching = ref(false);
// The chosen homes in full, for the amenities each would lose; read once the confirm step opens
const details = ref<PropertyDto[]>([]);
const detailsPending = ref(false);

const building = computed(
  () => props.building ?? buildings.value.find((candidate) => candidate.id === chosenBuildingId.value),
);
const { units } = useBuildingUnitsQuery(() => building.value?.id ?? '');
const viewer = computed(() => ({ investorId: investor.value?.id, isAdmin: isAdmin.value }));

// Only an apartment standing on its own can be a unit; the server refuses the rest
const candidates = computed(() =>
  properties.value.filter((home) => !home.buildingId && home.propertyType === PropertyType.APARTMENT),
);

const title = computed(() => {
  const target = building.value ? `to ${building.value.name}` : 'to a building';

  return step.value === 'pick'
    ? `Attach homes ${target}`
    : `Attach ${pluralize('home', chosen.value.length, true)} ${target}`;
});

/** The street as a key: "221 N. Main St" and "221 n main st" are one address */
function streetKey(address: string | null | undefined): string {
  return (address ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** The homes at the building's own address first, then those filed where the building is, then the rest */
const groups = computed(() => {
  const needle = search.value.trim().toLowerCase();
  const shown = needle
    ? candidates.value.filter((home) =>
        [home.displayName, home.address ?? ''].some((text) => text.toLowerCase().includes(needle)),
      )
    : candidates.value;
  const target = building.value;
  const atAddress = (home: PropertyListItemDto) =>
    Boolean(target) && streetKey(home.address) === streetKey(target?.location.address);
  const inPortfolio = (home: PropertyListItemDto) =>
    Boolean(target?.portfolio) && home.portfolio?.id === target?.portfolio?.id;

  return [
    { title: 'Same address as the building', count: true, homes: shown.filter(atAddress) },
    {
      title: `Other homes in ${target?.portfolio?.name ?? 'the portfolio'}`,
      count: false,
      homes: shown.filter((home) => !atAddress(home) && inPortfolio(home)),
    },
    {
      title: target ? 'Elsewhere in your company' : 'Your apartments',
      count: false,
      homes: shown.filter((home) => !atAddress(home) && !inPortfolio(home)),
    },
  ].filter((group) => group.homes.length > 0);
});

const chosen = computed(() => candidates.value.filter((home) => home.id in picked));

/** Why a home cannot be ticked: only who runs it may move it, as for filing into a portfolio */
function reasonNotToMove(home: PropertyListItemDto): string {
  if (runsUnit(viewer.value, home, portfolios.value)) {
    return '';
  }

  return home.manager
    ? `Managed by ${teamMemberName(home.manager)} — you can view it but not move it`
    : 'Without a manager — an admin can move it';
}

function subtitleOf(home: PropertyListItemDto): string {
  return (
    reasonNotToMove(home) ||
    [home.portfolio ? `In ${home.portfolio.name}` : '', propertySummary(home)].filter(Boolean).join(' · ')
  );
}

/** A number already on one of the building's units, or on another ticked home, before the server says so */
function numberError(homeId: string): string {
  const entry = picked[homeId];

  if (!entry) {
    return '';
  }

  if (entry.error) {
    return entry.error;
  }

  const number = entry.unitNumber.trim().toLowerCase();

  if (!number) {
    return '';
  }

  const taken = units.value.find((unit) => (unit.unitNumber ?? '').trim().toLowerCase() === number);

  if (taken) {
    return `#${entry.unitNumber.trim()} is already ${unitLabel(taken)}`;
  }

  const twice = Object.entries(picked).some(
    ([otherId, other]) => otherId !== homeId && other.unitNumber.trim().toLowerCase() === number,
  );

  return twice ? 'Given to another home here' : '';
}

const canContinue = computed(
  () =>
    Boolean(building.value) &&
    chosen.value.length > 0 &&
    chosen.value.every((home) => picked[home.id].unitNumber.trim() && !numberError(home.id)),
);

const summaryLine = computed(() =>
  chosen.value.map((home) => `${home.displayName} → #${picked[home.id].unitNumber.trim()}`).join(' · '),
);
const listedCount = computed(
  () =>
    chosen.value.filter((home) => [PropertyStatus.LISTED, PropertyStatus.LISTING_IN_PROGRESS].includes(home.status))
      .length,
);

const portfolioChange = computed(() => {
  const destination = building.value?.portfolio?.name ?? 'Unassigned';
  const sources = [...new Set(chosen.value.map((home) => home.portfolio?.name ?? 'Unassigned'))];

  return sources.length === 1 && sources[0] !== destination ? `${destination} (was: ${sources[0]})` : destination;
});

const managerChange = computed(() => {
  const manager = building.value?.manager;
  const lead = manager ? `${teamMemberName(manager)} and the building's team.` : "The building's team.";
  const previous = [
    ...new Set(
      chosen.value
        .filter((home) => home.manager && home.manager.investorId !== manager?.investorId)
        .map((home) => teamMemberName(home.manager!)),
    ),
  ];

  return previous.length
    ? `${lead} ${previous.join(', ')} ${previous.length === 1 ? 'stays' : 'stay'} on as a collaborator.`
    : lead;
});

/** The listed homes keep listing; the feeds learn the building's details on their own clock */
const listedNotice = computed(() => {
  if (!listedCount.value) {
    return '';
  }

  const who =
    listedCount.value === 1
      ? 'One of them is listed and stays listed'
      : `${listedCount.value} are listed and stay listed`;

  return ` ${who}; Zillow and the other feeds pick the changes up with their next sync, usually within a day.`;
});

const whatToExpectItems = computed<WhatToExpectItem[]>(() => {
  const first = chosen.value[0];
  const number = first ? picked[first.id].unitNumber.trim() : '';

  return [
    { icon: 'folder', title: 'Portfolio', description: portfolioChange.value },
    { icon: 'users-01', title: 'Manager & team', description: managerChange.value },
    {
      icon: 'marker-pin-01',
      title: 'Address',
      description: `Shown as "${building.value?.location.address ?? ''} #${number}" from now on: the building's address with the unit number.`,
    },
    {
      icon: 'link-01',
      title: 'Listing stays',
      description: 'Listing id, apply link, leads, showings, applications and history stay with the home.',
    },
    {
      icon: 'door-01',
      title: 'Own terms stay',
      description:
        "Pet policy, renter requirements, lease term, deposits and fees stay the home's; building defaults only seed new units.",
    },
  ];
});

/** Per home, the building-level amenities that read differently on the home and on the building */
const amenityDiffs = computed(() =>
  details.value
    .map((home) => ({
      home,
      rows: buildingAmenityKeys
        .filter((key) => Boolean(home.amenities?.[key]) !== Boolean(building.value?.amenities?.[key]))
        .map((key) => ({
          label: amenityNamesMap[key],
          home: Boolean(home.amenities?.[key]),
          building: Boolean(building.value?.amenities?.[key]),
        })),
    }))
    .filter((diff) => diff.rows.length > 0),
);

/** Ticked: an apartment number the listing already carries is its unit number until the owner says otherwise */
function pick(homeId: string) {
  const home = candidates.value.find((candidate) => candidate.id === homeId);

  picked[homeId] = { unitNumber: home?.unitNumber?.trim() ?? '', error: '' };
}

function toggle(homeId: string, checked: boolean) {
  if (checked) {
    pick(homeId);
  } else {
    delete picked[homeId];
  }
}

async function confirm() {
  step.value = 'confirm';
  detailsPending.value = true;

  try {
    details.value = (await Promise.all(chosen.value.map((home) => propertyService.getById(home.id)))).map(
      (response) => response.data,
    );
  } catch {
    // The diff is a courtesy; the confirm step reads fine without it
    details.value = [];
  } finally {
    detailsPending.value = false;
  }
}

async function attach() {
  const target = building.value;

  if (!target) {
    return;
  }

  const units = chosen.value.map((home) => ({ propertyId: home.id, unitNumber: picked[home.id].unitNumber.trim() }));
  isAttaching.value = true;

  try {
    await attachUnits({ buildingId: target.id, units });
  } catch (error) {
    if (serverErrorCode(error) === buildingErrorCodes.unitNumberTaken) {
      // The refusal names the number; its row shows it, back on the list
      const number = String(serverErrorDetail(error, 'unitNumber') ?? '')
        .trim()
        .toLowerCase();
      const row = units.find((unit) => unit.unitNumber.toLowerCase() === number);

      if (row) {
        picked[row.propertyId].error = extractErrorMessage(error, 'Another unit in this building has that number');
      }

      step.value = 'pick';
    } else {
      // Someone who can't change the building gets its access dialog, which holds this toast back
      alertsStore.catchError(error);
    }

    return;
  } finally {
    isAttaching.value = false;
  }

  modalRef.value?.close();
  alertsStore.success(`${pluralize('home', units.length, true)} attached to <b>${escapeHtml(target.name)}</b>`);
  emit('attached', target.id);
}

/** Ticks the given home; from a home's page, that is the home itself */
function open(preselectedId?: string) {
  reset();

  if (preselectedId) {
    pick(preselectedId);
  }

  modalRef.value?.show();
}

function reset() {
  step.value = 'pick';
  chosenBuildingId.value = '';
  search.value = '';
  details.value = [];
  Object.keys(picked).forEach((id) => delete picked[id]);
}

// A building picked later changes which numbers are taken, so a stale refusal goes
watch(chosenBuildingId, () => Object.values(picked).forEach((entry) => (entry.error = '')));

defineExpose({ open });
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.form {
  width: 100%;
}

.back {
  display: inline-flex;
  align-items: center;
  gap: var(--t-spacing-xs);
  cursor: pointer;
}

// Room for "Unit #" and a number like 12B beside the # prefix
.number {
  width: 6.5rem;
}

.search-icon {
  color: var(--t-text-color-tertiary);
}

.error {
  color: var(--t-color-red-500);
}

.status {
  margin-right: auto;
}

// On a phone the pair takes the whole row, the count above it
.actions {
  @include mobile {
    width: 100%;

    > * {
      flex: 1;
    }
  }
}

// Overlapping thumbs, one per home; a white ring keeps each one's edge
.thumbs {
  display: flex;

  > * + * {
    margin-left: calc(-1 * var(--t-spacing-sm));
  }

  > * {
    box-shadow: 0 0 0 2px var(--t-color-white);
    border-radius: var(--t-border-radius-sm);
  }
}

.identity {
  min-width: 0;
  overflow-wrap: anywhere;
}

.diff {
  padding: var(--t-spacing-sm) var(--t-spacing-md);
  border: 1px solid var(--t-color-border-secondary);
  border-radius: var(--t-border-radius-sm);
}

.diff-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;

  th,
  td {
    padding: var(--t-spacing-xs) 0;
    text-align: left;
  }

  th {
    font-weight: 500;
    color: var(--t-text-color-secondary);
  }

  // What the home says today and will no longer
  .lost {
    color: var(--t-text-color-tertiary);
    text-decoration: line-through;
  }
}
</style>
